import { Badge, Card, Group, SimpleGrid, Stack, Tabs, Text, Title, UnstyledButton } from '@mantine/core';
import { IconChecklist, IconArrowBackUp } from '@tabler/icons-react';
import { useState } from 'react';
import { Link } from 'react-router';
import type { TaskListItemDto } from '@mexp/shared';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/States';
import { formatBusinessDate, fromNow } from '@/lib/format';
import { useMyTasks } from '@/features/requests/api';
import { DueDate, TaskStatusBadge } from '@/features/requests/components/badges';

function TaskCard({ row }: { row: TaskListItemDto }) {
  const { item, request } = row;
  return (
    <Card component={Link} to={`/internal/my-tasks/${item.id}`} padding="md" style={{ textDecoration: 'none', color: 'inherit' }}>
      <Stack gap={8}>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Text fw={600} lineClamp={2}>
            {item.title}
          </Text>
          <TaskStatusBadge status={item.status} />
        </Group>
        <Text fz="sm" c="dimmed" lineClamp={1}>
          {request.productName} for {request.country} · {request.exporterName}
        </Text>
        <Group justify="space-between">
          <DueDate date={item.targetDate} open={item.status === 'assigned' || item.status === 'returned'} />
          <Text fz="xs" c="dimmed" ff="monospace">
            {item.code}
          </Text>
        </Group>
        {item.status === 'returned' && item.lastReturn && (
          <Group gap={6} wrap="nowrap">
            <IconArrowBackUp size={14} color="var(--mantine-color-orange-6)" />
            <Text fz="xs" c="orange.8" lineClamp={1}>
              {item.lastReturn.reason}
            </Text>
          </Group>
        )}
        {item.targetProposal && (
          <Badge size="sm" color="yellow" variant="light" w="fit-content">
            You proposed {formatBusinessDate(item.targetProposal.date)} · waiting for RA
          </Badge>
        )}
        {item.status === 'submitted' && item.lastSubmittedAt && (
          <Text fz="xs" c="dimmed">
            Submitted {fromNow(item.lastSubmittedAt)}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

function Group_({ title, hint, rows }: { title: string; hint?: string; rows: TaskListItemDto[] }) {
  if (!rows.length) return null;
  return (
    <Stack gap="sm">
      <Group gap="xs">
        <Title order={5}>{title}</Title>
        <Badge variant="light">{rows.length}</Badge>
        {hint && (
          <Text fz="sm" c="dimmed">
            {hint}
          </Text>
        )}
      </Group>
      <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }}>
        {rows.map((r) => (
          <TaskCard key={r.item.id} row={r} />
        ))}
      </SimpleGrid>
    </Stack>
  );
}

export function MyTasksPage() {
  const [tab, setTab] = useState<'open' | 'done'>('open');
  const open = useMyTasks('open');
  const done = useMyTasks('done');
  const current = tab === 'open' ? open : done;
  const rows = current.data ?? [];

  return (
    <>
      <PageHeader title="My tasks" description="Documents you have been asked to provide. Upload them here; RA verifies each one." />
      <Tabs value={tab} onChange={(v) => setTab((v as 'open' | 'done') ?? 'open')} mb="lg">
        <Tabs.List>
          <Tabs.Tab value="open" rightSection={open.data?.length ? <Badge size="sm">{open.data.length}</Badge> : null}>
            Open
          </Tabs.Tab>
          <Tabs.Tab value="done">Verified</Tabs.Tab>
        </Tabs.List>
      </Tabs>
      {current.isLoading ? (
        <LoadingState />
      ) : current.isError ? (
        <ErrorState error={current.error} onRetry={() => void current.refetch()} />
      ) : !rows.length ? (
        <EmptyState
          icon={IconChecklist}
          title={tab === 'open' ? "You're all caught up" : 'Nothing verified yet'}
          description={tab === 'open' ? 'New tasks appear here and in your notifications.' : undefined}
        />
      ) : tab === 'open' ? (
        <Stack gap="xl">
          <Group_ title="Needs changes" hint="RA sent these back" rows={rows.filter((r) => r.item.status === 'returned')} />
          <Group_ title="To do" rows={rows.filter((r) => r.item.status === 'assigned')} />
          <Group_ title="Waiting for verification" hint="You can still add files" rows={rows.filter((r) => r.item.status === 'submitted')} />
        </Stack>
      ) : (
        <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }}>
          {rows.map((r) => (
            <TaskCard key={r.item.id} row={r} />
          ))}
        </SimpleGrid>
      )}
      {tab === 'open' && rows.length > 0 && (
        <UnstyledButton mt="xl" onClick={() => setTab('done')}>
          <Text fz="sm" c="dimmed">
            See verified tasks →
          </Text>
        </UnstyledButton>
      )}
    </>
  );
}
